/* NIMBUS // Cargo Kit — QUICK VIEW (2026-08-07)
 * ─────────────────────────────────────────────────────────────────────────────
 * "ORBITAL CARGO INSPECTION." Triggered ONLY by the card's ⊹ QUICK VIEW link
 * (design system §5 — the card and image go to the product page; only this link
 * opens the overlay). Before this file the button rendered onclick="return false".
 *
 * ALL CSS ALREADY EXISTED in assets/nimbus-collection.css from the design export:
 * .nb-ov, .ovbg, .ovcross-v/h, .ovxy, .ovmodal, .ovbk-*, .ovclose, .ovleft/.ovright,
 * .ovpower (CRT power-on), .ovbeam, .ovstatus/.ovblink, .ovrow/.ovk/.nb-specv,
 * .ovbuy/.ovstep/.ovstepbtn/.ovqty, canvas.ovrain, plus the ≤820px stacking rule.
 * The only CSS added was .ovnav / .ovcount, and .ovnav is the product page's hero
 * nav pattern reused verbatim — not a new invention.
 *
 * DATA: shares templates/product.cargo-specs.liquid with compare — one endpoint, one
 * cache. Which specs appear is config (`quick` in snippets/nimbus-spec-fields.liquid),
 * never hardcoded here; this file contains no metafield key and no spec label (§13).
 *
 * KAKAROT's calls 2026-08-07: curated ~8 spec rows · cycle all images with arrows ·
 * add-to-cargo closes quick view and opens the cart drawer.
 */
(function () {
  'use strict';

  var cache = {};
  var inflight = {};
  var ov = null;
  var lastFocus = null;
  var state = null;   // { item, imgIndex, qty }
  var rainStop = null;

  function reduced() {
    return !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Retro modes kill decorative motion (design system §8). The scan-bay beam, the crosshair
  // converge, the SCANNING→COMPLETE beat and the sold-out rain are all decorative, so they are
  // skipped here rather than merely sped up.
  function retro() {
    var c = document.documentElement.className;
    return c.indexOf('mode-lisa') > -1 || c.indexOf('mode-classic') > -1 || c.indexOf('mode-crt') > -1;
  }

  function plain() { return reduced() || retro(); }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ── data (shared endpoint + cache with compare) ────────────────────────── */

  function fetchSpecs(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    if (inflight[handle]) return inflight[handle];
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '?view=cargo-specs';
    inflight[handle] = fetch(url, { headers: { 'X-Requested-With': 'fetch' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (j) { cache[handle] = j; delete inflight[handle]; return j; })
      .catch(function (e) { delete inflight[handle]; throw e; });
    return inflight[handle];
  }

  /* ── sold-out rain ──────────────────────────────────────────────────────── */

  // Alert-red matrix rain over the scan bay, per design system §6 (sold-out quick view =
  // red rain + OFFLINE // CARGO DEPLETED). Written locally because nimbus-mode.js binds its
  // ambient rain to the single #nb-rain element and exposes no reusable renderer.
  function startRain(canvas) {
    if (!canvas || !canvas.getContext || plain()) return null;
    var cx = canvas.getContext('2d');
    var glyphs = 'ｱｲｳｴｵｶｷｸｹｺ0123456789//';
    var cols = [];
    function size() {
      var r = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, r.width);
      canvas.height = Math.max(1, r.height);
      cols = new Array(Math.max(1, Math.floor(canvas.width / 14))).fill(0);
    }
    size();
    var iv = setInterval(function () {
      cx.fillStyle = 'rgba(4,5,10,.18)';
      cx.fillRect(0, 0, canvas.width, canvas.height);
      cx.fillStyle = '#ff5b5b';
      cx.font = "12px 'Space Mono',monospace";
      for (var i = 0; i < cols.length; i++) {
        cx.fillText(glyphs[Math.floor(Math.random() * glyphs.length)], i * 14, cols[i] * 14);
        if (cols[i] * 14 > canvas.height && Math.random() > 0.975) cols[i] = 0;
        cols[i]++;
      }
    }, 48);
    return function () { clearInterval(iv); };
  }

  /* ── open / close ───────────────────────────────────────────────────────── */

  function open(handle) {
    if (ov) return;   // one overlay at a time; a second would orphan the first keydown listener
    lastFocus = document.activeElement;

    ov = document.createElement('div');
    ov.className = 'nb-ov';
    ov.setAttribute('role', 'dialog');
    ov.setAttribute('aria-modal', 'true');
    ov.setAttribute('aria-label', 'Orbital cargo inspection');
    ov.innerHTML =
      '<div class="ovbg" data-qv-bg></div>' +
      (plain() ? '' : '<div class="ovcross-v"></div><div class="ovcross-h"></div><div class="ovxy">SCAN</div>') +
      '<div class="ovmodal" data-qv-modal>' +
        '<span class="ovbk ovbk-tl"></span><span class="ovbk ovbk-tr"></span>' +
        '<span class="ovbk ovbk-bl"></span><span class="ovbk ovbk-br"></span>' +
        '<button type="button" class="ovclose" data-qv-close aria-label="Close inspection">×</button>' +
        '<div class="ovleft" data-qv-left></div>' +
        '<div class="ovright" data-qv-right></div>' +
      '</div>';
    document.body.appendChild(ov);
    document.documentElement.style.overflow = 'hidden';

    ov.querySelector('[data-qv-bg]').addEventListener('click', close);
    ov.querySelector('[data-qv-close]').addEventListener('click', close);
    ov.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey, true);

    renderLoading();

    fetchSpecs(handle).then(function (item) {
      if (!ov) return;
      state = { item: item, imgIndex: 0, qty: 1 };
      render();
    }).catch(function () {
      if (!ov) return;
      var right = ov.querySelector('[data-qv-right]');
      if (right) right.innerHTML = '<div class="ovstatus" style="color:#ff5b5b;">// SCAN FAILED</div>' +
        '<div class="ovk" style="margin-top:10px;">Could not load this unit. Open the product page instead.</div>';
    });
  }

  function close() {
    if (!ov) return;
    if (rainStop) { rainStop(); rainStop = null; }
    document.removeEventListener('keydown', onKey, true);
    var dead = ov;
    ov = null;
    state = null;
    document.documentElement.style.overflow = '';
    // .nb-ov.closing reverses the entry animation; skip the wait entirely when motion is off.
    if (plain()) { dead.remove(); }
    else {
      dead.classList.add('closing');
      setTimeout(function () { dead.remove(); }, 300);
    }
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  function focusables() {
    if (!ov) return [];
    return Array.prototype.slice.call(ov.querySelectorAll(
      'button:not([disabled]), a[href], input, select, [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
  }

  function onKey(e) {
    if (!ov) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    // Arrow keys drive the scan bay, matching the product page gallery.
    if (state && state.item && (state.item.images || []).length > 1) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); stepImage(-1); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); stepImage(1); return; }
    }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function onClick(e) {
    var t = e.target;
    if (!t.closest) return;
    var nav = t.closest('[data-qv-img]');
    if (nav) { stepImage(parseInt(nav.getAttribute('data-qv-img'), 10) || 1); return; }
    var step = t.closest('[data-qv-step]');
    if (step) { stepQty(parseInt(step.getAttribute('data-qv-step'), 10) || 1); return; }
    var add = t.closest('[data-qv-add]');
    if (add) { addToCargo(add); return; }
  }

  /* ── rendering ──────────────────────────────────────────────────────────── */

  function renderLoading() {
    var left = ov.querySelector('[data-qv-left]');
    var right = ov.querySelector('[data-qv-right]');
    left.innerHTML = '<div class="ovtag">[ SCAN BAY ]</div>' +
      '<div class="ovpower" style="margin-top:14px;"></div>';
    right.innerHTML = '<div class="ovtag">[ CARGO INSPECTION ]</div>' +
      '<div class="ovstatus"><span class="ovblink">SCANNING UNIT…</span></div>';
  }

  function stepImage(dir) {
    if (!state || !ov) return;
    var imgs = state.item.images || [];
    if (imgs.length < 2) return;
    state.imgIndex = (state.imgIndex + dir + imgs.length) % imgs.length;
    var el = ov.querySelector('[data-qv-photo]');
    var counter = ov.querySelector('.ovcount');
    if (el) el.src = imgs[state.imgIndex];
    if (counter) counter.textContent = '// IMAGE ' + pad(state.imgIndex + 1) + ' / ' + pad(imgs.length);
  }

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function stepQty(dir) {
    if (!state || !ov) return;
    var cap = state.item.stock_cap;
    var next = state.qty + dir;
    if (next < 1) next = 1;
    // Stepper is capped at real stock so a shopper can't build a quantity checkout will reject.
    // A null cap means the variant doesn't track inventory or continues selling — no ceiling.
    if (cap !== null && cap !== undefined && next > cap) next = cap;
    state.qty = next;
    var q = ov.querySelector('[data-qv-qty]');
    if (q) q.textContent = pad(state.qty);
  }

  function render() {
    if (!ov || !state) return;
    var it = state.item;
    var imgs = it.images || [];
    var left = ov.querySelector('[data-qv-left]');
    var right = ov.querySelector('[data-qv-right]');
    var soldOut = !it.available;

    /* ---- LEFT: scan bay ---- */
    var photo = imgs.length
      ? '<img data-qv-photo src="' + esc(imgs[0]) + '" alt="' + esc(it.title) + '" ' +
        'style="position:absolute;inset:0;width:100%;height:100%;object-fit:contain;object-position:center;padding:14px;box-sizing:border-box;">'
      : '<div class="ovk" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;">DROP SCAN</div>';

    var nav = imgs.length > 1
      ? '<button type="button" class="ovnav prev" data-qv-img="-1" aria-label="Previous image">&lt;&lt;</button>' +
        '<button type="button" class="ovnav next" data-qv-img="1" aria-label="Next image">&gt;&gt;</button>' +
        '<span class="ovcount">// IMAGE 01 / ' + pad(imgs.length) + '</span>'
      : '';

    // Sold-out: red rain sits in the scan bay. The PHOTO STAYS CLEAN elsewhere on the site
    // (§6), but the design system specifies rain here for the inspection view specifically.
    var rainCanvas = soldOut && !plain() ? '<canvas class="ovrain" aria-hidden="true"></canvas>' : '';
    var beam = plain() ? '' : '<span class="ovbeam"></span>';

    left.innerHTML =
      '<div class="ovtag">[ SCAN BAY ]</div>' +
      '<div class="ovpower" style="margin-top:14px;">' + photo + rainCanvas + beam + nav + '</div>' +
      '<div class="ovstatus" style="color:' + (soldOut ? '#ff5b5b' : 'var(--nb-acid-lime,var(--nb-acc))') + ';">' +
        (soldOut ? 'OFFLINE // CARGO DEPLETED' : 'SCAN COMPLETE') +
      '</div>' +
      '<div class="ovchips"><a class="ovtag" href="' + esc(it.url) + '" style="text-decoration:underline;">' +
        '&gt;&gt; FULL SPEC MANIFEST</a></div>';

    /* ---- RIGHT: inspection panel ---- */
    // Spec rows come from the `quick` key list in config order; rows with no value are dropped.
    var byKey = {};
    (it.rows || []).forEach(function (r) { byKey[r.k] = r; });
    var specRows = (it.quick || []).map(function (k) { return byKey[k]; })
      .filter(function (r) { return r && r.v; })
      .map(function (r) {
        return '<div class="ovrow"><span class="ovk">' + esc(r.l) + '</span>' +
          '<span class="nb-specv">' + esc(r.v) + '</span></div>';
      }).join('');

    var buy;
    if (soldOut) {
      buy = '<div class="ovbuy"><button type="button" class="nb-cargobtn is-soldout" disabled>' +
        '<span class="nb-cargolabel">SOLD OUT</span></button></div>';
    } else if (it.variant_count > 1 || !it.variant_id) {
      // Refuse to guess which variant the shopper wants. Quick view has no variant selector
      // (none was designed, and inventing one would mean adding a pattern to the kit first),
      // so a multi-variant product is handed to the product page rather than silently
      // adding whichever variant happened to come back first.
      buy = '<div class="ovbuy"><a class="nb-cargobtn" href="' + esc(it.url) + '" ' +
        'style="display:flex;align-items:center;justify-content:center;text-decoration:none;">' +
        '<span class="nb-cargolabel">SELECT OPTIONS</span></a></div>';
    } else {
      buy = '<div class="ovbuy">' +
        '<div class="ovstep">' +
          '<button type="button" class="ovstepbtn" data-qv-step="-1" aria-label="Decrease quantity">−</button>' +
          '<span class="ovqty" data-qv-qty aria-live="polite">01</span>' +
          '<button type="button" class="ovstepbtn" data-qv-step="1" aria-label="Increase quantity">+</button>' +
        '</div>' +
        '<button type="button" class="nb-cargobtn" data-qv-add="' + esc(it.variant_id) + '" style="flex:1 1 auto;">' +
          '<span class="nb-cargolabel">ADD TO CARGO</span>' +
        '</button>' +
      '</div>';
    }

    var stockChip = '';
    if (!soldOut && it.stock_cap !== null && it.stock_cap !== undefined) {
      stockChip = '<div class="ovk" style="margin-top:8px;">// STOCK ' + pad(it.stock_cap) + '</div>';
    }

    right.innerHTML =
      '<div class="ovtag">[ CARGO INSPECTION ]</div>' +
      '<div class="ovk" style="margin-top:10px;">' + esc(it.vendor || '') + '</div>' +
      '<a href="' + esc(it.url) + '" class="nb-specv" style="text-align:left;display:block;margin-top:4px;font-size:calc(15px * var(--nb-tscale));text-decoration:none;">' + esc(it.title) + '</a>' +
      '<div style="margin-top:12px;font-family:\'Space Mono\',monospace;font-weight:700;font-size:calc(20px * var(--nb-tscale));color:var(--nb-acid-lime,var(--nb-acc));">' + esc(it.price) + '</div>' +
      buy + stockChip +
      '<div class="ovtag" style="margin-top:20px;">// SPEC MANIFEST</div>' +
      '<div class="ovspecs" style="margin-top:6px;">' + (specRows || '<div class="ovk">// NO SPEC MANIFEST PUBLISHED</div>') + '</div>';

    if (soldOut && !plain()) {
      rainStop = startRain(ov.querySelector('canvas.ovrain'));
    }

    var c = ov.querySelector('[data-qv-close]');
    if (c) c.focus();
  }

  /* ── add to cargo ───────────────────────────────────────────────────────── */

  function addToCargo(btn) {
    if (!state || btn.disabled) return;
    var id = btn.getAttribute('data-qv-add');
    if (!id) return;
    var label = btn.querySelector('.nb-cargolabel');
    var prev = label ? label.textContent : '';
    btn.disabled = true;
    if (label) label.textContent = '… ADDING';

    var drawer = document.querySelector('cart-drawer');
    var payload = { id: Number(id), quantity: state.qty };
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      payload.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
      payload.sections_url = window.location.pathname;
    }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    fetch(root.replace(/\/$/, '') + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); }).then(function (json) {
      if (json && json.status) throw new Error(json.description || 'add failed');
      // KAKAROT's call: close quick view, then open the drawer. Two stacked modals would
      // fight over the focus trap.
      if (drawer && json && json.sections) {
        close();
        drawer.renderContents(json);
      } else {
        if (label) label.textContent = '✓ IN CARGO';
        setTimeout(function () { btn.disabled = false; if (label) label.textContent = prev; }, 1400);
      }
    }).catch(function () {
      if (label) label.textContent = '✕ COULD NOT ADD';
      setTimeout(function () { btn.disabled = false; if (label) label.textContent = prev; }, 1800);
    });
  }

  /* ── boot ───────────────────────────────────────────────────────────────── */

  // Delegated at document level for the same reason as compare: cards are rebuilt by every
  // AJAX facet swap and shuttled into .orbit-card wrappers by ORBIT.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-nimbus-quickview]');
    if (!btn) return;
    e.preventDefault();
    var card = btn.closest('.nb-card');
    var handle = (card && card.dataset && card.dataset.handle) || btn.getAttribute('data-handle');
    if (handle) open(handle);
  });

  window.NimbusQuickView = { open: open, close: close };
})();
